import React, { useEffect, useRef, useState, useCallback } from 'react'
import styles from './ExperienceSection.module.css'
import { experience } from '../../../data/experience'
import { useScrollAnimation } from '../../../hooks/useScrollAnimation'

const ExperienceSection = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.3 })
  const [lineHeight, setLineHeight] = useState(0)
  const [isInView, setIsInView] = useState(false)
  const [activeCardIndex, setActiveCardIndex] = useState(0)
  const [activeDots, setActiveDots] = useState([])
  const timelineRef = useRef(null)
  const sectionRef = useRef(null)
  const cardRefs = useRef([])
  const dotRefs = useRef([])

  // Real-time scroll tracking for the timeline progress
  const updateTimelineProgress = useCallback(() => {
    if (!timelineRef.current || !sectionRef.current) return

    const timelineRect = timelineRef.current.getBoundingClientRect()
    const windowHeight = window.innerHeight

    // Check if timeline is in view
    const inView = timelineRect.top < windowHeight && timelineRect.bottom > 0
    setIsInView(inView)

    if (inView) {
      // Calculate where the "scan line" should be
      // The line follows a point that starts at the top of viewport when timeline enters
      // and ends at bottom of timeline when we've scrolled past
      const scanLineY = windowHeight * 0.4 // The "reading point" - 40% down the viewport

      // How far down the timeline is our scan line?
      const timelineTop = timelineRect.top
      const timelineHeight = timelineRect.height

      // Progress: 0 when scan line is at timeline top, 1 when at timeline bottom
      const progressPx = scanLineY - timelineTop
      const progress = Math.max(0, Math.min(1, progressPx / timelineHeight))

      setLineHeight(progress * 100)

      // Check which dots the progress line has reached
      const newActiveDots = []
      dotRefs.current.forEach((dotRef, index) => {
        if (dotRef) {
          const dotRect = dotRef.getBoundingClientRect()
          const dotCenter = dotRect.top + dotRect.height / 2
          // Dot is active if the glow line has passed it
          if (dotCenter <= scanLineY) {
            newActiveDots.push(index)
          }
        }
      })
      setActiveDots(newActiveDots)
    }

    // Track which card is most visible (for mobile accent)
    const viewportCenter = windowHeight * 0.4
    let closestIndex = 0
    let closestDistance = Infinity

    cardRefs.current.forEach((ref, index) => {
      if (ref) {
        const rect = ref.getBoundingClientRect()
        const cardCenter = rect.top + rect.height / 2
        const distance = Math.abs(cardCenter - viewportCenter)

        // Only consider cards that are at least partially visible
        if (rect.top < windowHeight && rect.bottom > 0 && distance < closestDistance) {
          closestDistance = distance
          closestIndex = index
        }
      }
    })

    setActiveCardIndex(closestIndex)
  }, [])

  useEffect(() => {
    // Initial calculation
    updateTimelineProgress()

    // Update on scroll
    window.addEventListener('scroll', updateTimelineProgress, { passive: true })
    window.addEventListener('resize', updateTimelineProgress, { passive: true })

    return () => {
      window.removeEventListener('scroll', updateTimelineProgress)
      window.removeEventListener('resize', updateTimelineProgress)
    }
  }, [updateTimelineProgress])

  return (
    <section id="experience" className={styles.experience} ref={sectionRef}>
      <div className={styles.container}>
        <div
          ref={headerRef}
          className={styles.header}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <span className={styles.sectionLabel}>Work Experience</span>
          <h2 className={styles.sectionTitle}>My Professional Journey</h2>
          <p className={styles.sectionDescription}>
            A decade of building innovative solutions and driving digital transformation
          </p>
        </div>

        <div className={styles.timeline} ref={timelineRef}>
          {/* Static background line */}
          <div className={styles.timelineLine}></div>

          {/* Animated glowing line */}
          <div
            className={styles.timelineGlow}
            style={{ height: `${lineHeight}%` }}
          />

          {experience.map((job, index) => (
            <div
              key={job.id}
              ref={(el) => (cardRefs.current[index] = el)}
              className={`${styles.timelineItem} ${index % 2 === 0 ? styles.left : styles.right} ${activeCardIndex === index ? styles.activeCard : ''}`}
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView
                  ? 'translateX(0)'
                  : index % 2 === 0 ? 'translateX(-50px)' : 'translateX(50px)',
                transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.15}s`
              }}
            >
              <div className={styles.timelineContent}>
                <div className={styles.timelineCard}>
                  {job.current && (
                    <span className={styles.currentBadge}>Current</span>
                  )}

                  {job.roles ? (
                    <>
                      <h3 className={styles.jobTitle}>{job.company}</h3>
                      <div className={styles.jobMeta}>
                        <span className={styles.location}>{job.location}</span>
                      </div>
                      <div className={styles.period}>{`${job.roles.length} roles`}</div>

                      <p className={styles.description}>{job.description}</p>

                      <div className={styles.progression}>
                        <h4>Career progression</h4>
                        <ul className={styles.roleList}>
                          {job.roles.map((role, idx) => (
                            <li
                              key={idx}
                              className={`${styles.roleItem} ${idx === 0 ? styles.roleLatest : ''}`}
                            >
                              <div className={styles.roleHeader}>
                                <span className={styles.roleTitle}>{role.title}</span>
                              </div>
                              <p className={styles.roleSummary}>{role.summary}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className={styles.jobTitle}>{job.title}</h3>
                      <div className={styles.jobMeta}>
                        <span className={styles.company}>{job.company}</span>
                        <span className={styles.separator}>•</span>
                        <span className={styles.location}>{job.location}</span>
                      </div>
                      <p className={styles.description}>{job.description}</p>

                      <div className={styles.responsibilities}>
                        <h4>Key Responsibilities:</h4>
                        <ul>
                          {job.responsibilities.slice(0, 3).map((resp, idx) => (
                            <li key={idx}>{resp}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}

                  <div className={styles.achievements}>
                    <h4>Notable Achievements:</h4>
                    <ul>
                      {job.achievements.slice(0, job.roles ? 3 : 2).map((achievement, idx) => (
                        <li key={idx}>{achievement}</li>
                      ))}
                    </ul>
                  </div>

                  <div className={styles.technologies}>
                    {job.technologies.map((tech, idx) => (
                      <span key={idx} className={styles.techBadge}>{tech}</span>
                    ))}
                  </div>
                </div>

                <div
                  ref={(el) => (dotRefs.current[index] = el)}
                  className={`${styles.timelineDot} ${activeDots.includes(index) ? styles.dotActive : ''}`}
                >
                  <span></span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ExperienceSection